import React from "react"
import { useFlags } from "launchdarkly-react-client-sdk"
import { Link as RouterLink, Redirect, useParams } from "react-router-dom"
import { Box, Divider, Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { PageLayout } from "#/features/global-ui/page-layout"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { useHelpCenterCategoryBySlug } from "#/services/help-center/useGetHelpCenter"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { helpCenterArticlePath } from "#/services/help-center/urls"
import type { HelpCenterCategory, HelpCenterSubCategory } from "#/services/help-center/types"

export function FaqsCategoryPage(): JSX.Element {
  const { contentfulHelpCenter } = useFlags()

  if (!contentfulHelpCenter) {
    return <Redirect to="/" />
  }

  return <FaqsCategoryPageContent />
}

function FaqsCategoryPageContent(): JSX.Element {
  const { categorySlug } = useParams<{ categorySlug: string }>()
  const { data: category, isLoading, isError } = useHelpCenterCategoryBySlug(categorySlug)

  if (isLoading) {
    return <FullScreenLoading />
  }

  if (isError) {
    return (
      <PageLayout>
        <ErrorPage />
      </PageLayout>
    )
  }

  if (!category) {
    return <Redirect to={Paths.FAQS} />
  }

  return (
    <>
      <Title title={category.title} productTitle="FAQs" />
      <PageLayout>
        <Typography variant="h2">{category.title}</Typography>
        <CategorySections category={category} />
      </PageLayout>
    </>
  )
}

function CategorySections({ category }: { category: HelpCenterCategory }): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      divider={<Divider />}
      spacing={theme.tokens.spacing.xl}
      sx={(t) => ({ marginBlockStart: t.spacing(t.tokens.spacing.xl) })}
    >
      {category.subCategories.map((subCategory) => (
        <SubCategorySection key={subCategory.id} subCategory={subCategory} />
      ))}
    </Stack>
  )
}

function SubCategorySection({ subCategory }: { subCategory: HelpCenterSubCategory }): JSX.Element {
  const theme = useTheme()
  return (
    <Box>
      <Typography variant="h5" sx={(t) => ({ marginBlockEnd: t.spacing(t.tokens.spacing.md) })}>
        {subCategory.title}
      </Typography>
      <Stack
        direction="column"
        spacing={theme.tokens.spacing.xs}
        component="ul"
        sx={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        {subCategory.articles.map((article) => (
          <Box key={article.id} component="li">
            <Link component={RouterLink} to={helpCenterArticlePath(article)} variant="body1">
              {article.title}
            </Link>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
